import { Customer } from '../../types';

// Auswahl-/Suchlogik für die Kunden-Zuweisung im Projekt (ProjectCustomerLink).
// Bewusst als pure Funktionen, damit sie ohne DOM-Testumgebung prüfbar sind
// (siehe customerLinkLogic.test.ts) — analog projectHubLogic.ts.

/**
 * Auswählbar ist ein Kunde nur, wenn er weder archiviert noch in einen anderen
 * Kunden gemerged wurde. Beides sind "tote" Stammsätze: eine neue Verknüpfung
 * darauf würde Rechnungen und Kundenakte ins Leere laufen lassen.
 */
export const isSelectableCustomer = (c: Customer): boolean =>
    !c.archivedAt && !c.mergedIntoId;

export const normalizeSearch = (s?: string | null): string =>
    (s || '').toLowerCase().replace(/\s+/g, ' ').trim();

// Rechtsformen und Füllwörter tragen nichts zur Ähnlichkeit bei: fast jeder
// Auftraggeber ist eine "GmbH", das darf keine Treffer erzeugen.
const NOISE_TOKENS = new Set([
    'gmbh', 'mbh', 'ag', 'kg', 'ohg', 'gbr', 'ug', 'se', 'ev', 'co', 'cokg',
    'und', 'der', 'die', 'das', 'gmbhco', 'ggmbh', 'kgaa', 'partg', 'mbb',
]);

/**
 * Vergleichbare Namensbestandteile: Sonderzeichen raus, kurze Wörter und
 * Rechtsformen raus. "Müller Bau GmbH & Co. KG" -> ["müller", "bau"].
 */
export const nameTokens = (s?: string | null): string[] =>
    normalizeSearch(s)
        .replace(/[^a-z0-9äöüß]+/g, ' ')
        .split(' ')
        .filter((t) => t.length > 2 && !NOISE_TOKENS.has(t));

/**
 * Wie stark ähnelt der Kundenname den Namen aus den Projekt-Metadaten
 * (Auftraggeber / Bauherr)? Anzahl gemeinsamer Namensbestandteile — grob, aber
 * ausreichend, um die wahrscheinlichen Kandidaten nach oben zu sortieren.
 */
export const contextAffinity = (customerName: string | undefined, contextNames: (string | undefined)[]): number => {
    const tokens = nameTokens(customerName);
    if (tokens.length === 0) return 0;
    const set = new Set(tokens);
    let best = 0;
    for (const ctx of contextNames) {
        const hits = nameTokens(ctx).filter((t) => set.has(t)).length;
        if (hits > best) best = hits;
    }
    return best;
};

/**
 * Trefferstärke einer Suchanfrage gegen einen Kunden.
 * 0 = kein Treffer (Kunde wird ausgefiltert), höher = besser.
 */
export const searchScore = (c: Customer, query: string): number => {
    const q = normalizeSearch(query);
    if (!q) return 1; // Ohne Suche ist jeder Kunde gleichwertig sichtbar.
    const name = normalizeSearch(c.name);
    const number = normalizeSearch(c.customerNumber);
    if (number && number === q) return 5;
    if (name.startsWith(q)) return 4;
    if (number.startsWith(q)) return 3;
    if (name.includes(q)) return 2;
    if (normalizeSearch(c.city).includes(q) || normalizeSearch(c.address).includes(q) || normalizeSearch(c.email).includes(q)) return 1;
    return 0;
};

export interface RankCustomerOptions {
    /** Freitextsuche über Name, Kundennummer, Ort/Adresse, E-Mail. */
    query?: string;
    /** Namen aus den Projekt-Metadaten (Auftraggeber, Bauherr) für die Vorsortierung. */
    contextNames?: (string | undefined)[];
    /** Bereits verknüpfter Kunde — bleibt sichtbar, auch wenn er archiviert wurde. */
    currentCustomerId?: string | null;
    limit?: number;
}

/**
 * Auswahlliste für den Kunden-Picker: archivierte/gemergte Kunden raus,
 * Suchtreffer gefiltert, dann nach Trefferstärke, Kontext-Ähnlichkeit und
 * Name sortiert. Der aktuell verknüpfte Kunde bleibt immer enthalten, damit
 * eine Alt-Verknüpfung auf einen archivierten Satz sichtbar bleibt.
 */
export const rankCustomerCandidates = (
    customers: Customer[],
    { query = '', contextNames = [], currentCustomerId = null, limit }: RankCustomerOptions = {},
): Customer[] => {
    const scored = customers
        .filter((c) => isSelectableCustomer(c) || c.id === currentCustomerId)
        .map((c) => ({ c, score: searchScore(c, query), affinity: contextAffinity(c.name, contextNames) }))
        .filter((e) => e.score > 0);

    scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        if (b.affinity !== a.affinity) return b.affinity - a.affinity;
        return normalizeSearch(a.c.name).localeCompare(normalizeSearch(b.c.name), 'de');
    });

    const out = scored.map((e) => e.c);
    return typeof limit === 'number' ? out.slice(0, limit) : out;
};

/** Anzeigename inkl. Kundennummer: "Müller Bau GmbH (K-1042)". */
export const formatCustomerLabel = (c?: Customer | null): string => {
    if (!c) return '';
    const name = (c.name || '').trim() || 'Unbenannter Kunde';
    return c.customerNumber ? `${name} (${c.customerNumber})` : name;
};

/**
 * Auftraggeber- und Bauherr-Name aus einer rohen Projektzeile (projects-Row
 * mit data.metadata). In der Buchhaltung liegt das Projekt nur als Rohzeile
 * vor, nicht als Report — der Picker braucht die Namen aber genauso wie in den
 * Stammdaten, um die wahrscheinlichen Kandidaten nach oben zu sortieren.
 */
export const projectContextNames = (
    project: unknown,
): { customerName?: string; clientName?: string } => {
    const md = (project as { data?: { metadata?: Record<string, unknown> } } | null | undefined)
        ?.data?.metadata ?? {};
    const clean = (v: unknown): string | undefined => {
        const s = typeof v === 'string' ? v.trim() : '';
        return s || undefined;
    };
    return { customerName: clean(md.customerName), clientName: clean(md.clientName) };
};

/**
 * Meldung, wenn für ein Projekt ohne Kunden ein Beleg entstehen soll.
 *
 * Wer zuweisen darf, bekommt sie gar nicht zu sehen — für ihn öffnet sich der
 * Picker. Für alle anderen muss dranstehen, wer den Zustand auflösen kann,
 * sonst ist die Meldung eine Sackgasse.
 */
export const missingCustomerMessage = (
    projectName?: string | null,
    opts: { canAssign?: boolean } = {},
): string => {
    const label = (projectName || '').trim();
    const subject = label ? `Dem Projekt "${label}"` : 'Diesem Projekt';
    const base = `${subject} ist kein Kunde zugeordnet. Ohne Rechnungsempfänger kann keine Rechnung entstehen.`;
    return opts.canAssign
        ? base
        : `${base} Ein Administrator muss dem Projekt einen Kunden zuweisen (Projekt → Stammdaten → Kunde (CRM)).`;
};

/**
 * Welche Aktion löst die Auswahl aus? "assign" = erste Zuweisung (ohne
 * Rückfrage), "change" = bestehende Verknüpfung wird ersetzt (Bestätigung
 * nötig, weil Rechnungen und Kundenakte daran hängen), "noop" = gleicher Kunde.
 */
export const resolveLinkAction = (
    currentCustomerId: string | null | undefined,
    nextCustomerId: string | null,
): 'assign' | 'change' | 'remove' | 'noop' => {
    const cur = currentCustomerId || null;
    if (cur === nextCustomerId) return 'noop';
    if (nextCustomerId === null) return 'remove';
    return cur ? 'change' : 'assign';
};
